import { useState, useEffect } from "react";
import { getActivityLogs, ACTIVITY_TYPES, SEVERITY_LEVELS } from "../services/activityLogger";
import { FaEye, FaTrash, FaEdit, FaCheck, FaTimes, FaUserPlus, FaKey, FaSignInAlt, FaSignOutAlt } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const { user, hasRole, hasPermission, ROLES, PERMISSIONS } = useAuth();
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        const result = await getActivityLogs(100);
        if (result.success) {
            setLogs(result.logs);
        } else {
            console.error('Error fetching logs:', result.error);
        }
        setLoading(false);
    };

    const handleDeleteLog = async (logId) => {
        if (!window.confirm("Are you sure you want to delete this log entry? This action cannot be undone.")) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'logs', logId));

            // Remove from local state
            setLogs(prevLogs => prevLogs.filter(log => log.id !== logId));

            alert('Log entry deleted successfully');
        } catch (error) {
            console.error('Error deleting log:', error);
            alert('Failed to delete log entry. Please try again.');
        }
    };

    const handleToggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleToggleSelectAll = () => {
        if (selectAll) {
            setSelectedIds([]);
            setSelectAll(false);
        } else {
            const allIds = filteredLogs.map(l => l.id);
            setSelectedIds(allIds);
            setSelectAll(true);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedIds.length} selected log(s)? This cannot be undone.`)) return;
        try {
            // Delete sequentially to keep it simple and robust
            for (const id of selectedIds) {
                await deleteDoc(doc(db, 'logs', id));
            }
            setLogs(prev => prev.filter(l => !selectedIds.includes(l.id)));
            setSelectedIds([]);
            setSelectAll(false);
            alert('Selected logs deleted successfully');
        } catch (error) {
            console.error('Bulk delete error:', error);
            alert('Failed to delete some logs. Please try again.');
        }
    };

    const getActivityIcon = (activityType) => {
        switch (activityType) {
            case ACTIVITY_TYPES.CUSTOMER_DELETED:
            case ACTIVITY_TYPES.SERVICEMAN_DELETED:
            case ACTIVITY_TYPES.USER_DELETED:
                return <FaTrash style={{ color: '#dc3545' }} />;
            case ACTIVITY_TYPES.SERVICEMAN_UPDATED:
            case ACTIVITY_TYPES.USER_UPDATED:
                return <FaEdit style={{ color: '#ffc107' }} />;
            case ACTIVITY_TYPES.SERVICEMAN_APPROVED:
                return <FaCheck style={{ color: '#28a745' }} />;
            case ACTIVITY_TYPES.SERVICEMAN_REJECTED:
                return <FaTimes style={{ color: '#dc3545' }} />;
            case ACTIVITY_TYPES.USER_CREATED:
                return <FaUserPlus style={{ color: '#17a2b8' }} />;
            case ACTIVITY_TYPES.USER_PASSWORD_CHANGED:
                return <FaKey style={{ color: '#6f42c1' }} />;
            case ACTIVITY_TYPES.LOGIN:
                return <FaSignInAlt style={{ color: '#28a745' }} />;
            case ACTIVITY_TYPES.LOGOUT:
                return <FaSignOutAlt style={{ color: '#6c757d' }} />;
            default:
                return <FaEye style={{ color: '#007bff' }} />;
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case SEVERITY_LEVELS.HIGH:
                return '#dc3545';
            case SEVERITY_LEVELS.MEDIUM:
                return '#ffc107';
            case SEVERITY_LEVELS.LOW:
                return '#28a745';
            default:
                return '#6c757d';
        }
    };

    const getActivityDescription = (log) => {
        const { activityType, details, username, userName } = log;

        switch (activityType) {
            case ACTIVITY_TYPES.CUSTOMER_DELETED:
                return `${username} deleted customer: ${details.customerName || 'Unknown'}`;
            case ACTIVITY_TYPES.SERVICEMAN_DELETED:
                return `${username} deleted serviceman: ${details.servicemanName || 'Unknown'}`;
            case ACTIVITY_TYPES.SERVICEMAN_UPDATED:
                return `${username} updated serviceman: ${details.servicemanName || 'Unknown'}`;
            case ACTIVITY_TYPES.SERVICEMAN_APPROVED:
                return `${username} approved serviceman: ${details.servicemanName || 'Unknown'}`;
            case ACTIVITY_TYPES.SERVICEMAN_REJECTED:
                return `${username} rejected serviceman: ${details.servicemanName || 'Unknown'}`;
            case ACTIVITY_TYPES.USER_CREATED:
                return `${username} created ${details.userRole || 'user'}: ${details.userName || 'Unknown'}`;
            case ACTIVITY_TYPES.USER_UPDATED:
                return `${username} updated ${details.userRole || 'user'}: ${details.userName || 'Unknown'}`;
            case ACTIVITY_TYPES.USER_DELETED:
                return `${username} deleted ${details.userRole || 'user'}: ${details.userName || 'Unknown'}`;
            case ACTIVITY_TYPES.USER_PASSWORD_CHANGED:
                return `${username} changed password for: ${details.userName || 'Unknown'}`;
            case ACTIVITY_TYPES.LOGIN:
                return `${username} logged in`;
            case ACTIVITY_TYPES.LOGOUT:
                return `${username} logged out`;
            default:
                return `${username} performed ${activityType}`;
        }
    };

    const filteredLogs = logs.filter(log => {
        const matchesFilter = filter === "all" || log.activityType === filter;
        const matchesSearch = searchTerm === "" ||
            log.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.details?.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.details?.servicemanName?.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesFilter && matchesSearch;
    });

    // Check if user has permission to view activity logs
    if (!hasPermission(PERMISSIONS.USERS.MANAGE)) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                fontSize: '18px',
                color: '#dc2626',
                textAlign: 'center',
                flexDirection: 'column',
                gap: '10px'
            }}>
                <div>🚫 Access Denied</div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    You don't have permission to view activity logs. Only Super Admins can access this page.
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                fontSize: '18px',
                color: '#666'
            }}>
                <span className="loader"></span>
                <style>{`
                    .loader {
                        width: 48px;
                        height: 48px;
                        border-radius: 50%;
                        display: inline-block;
                        position: relative;
                        border: 2px solid #FF3D00;
                        box-sizing: border-box;
                        animation: rotation 1s linear infinite;
                    }
                    .loader::after {
                        content: '';  
                        box-sizing: border-box;
                        position: absolute;
                        left: 4px;
                        top: 4px;
                        border: 2px solid #000;
                        width: 12px;
                        height: 12px;
                        border-radius: 50%;
                    }
                    [data-theme="dark"] .loader::after {
                        border: 2px solid #FFF;
                    }
                    @keyframes rotation {
                        0% {
                            transform: rotate(0deg);
                        }
                        100% {
                            transform: rotate(360deg);
                        }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{ padding: '30px', margin: '0 auto' }}>
            <style>{`
        .activity-logs {
          background: var(--bg-primary, #f9fafb);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.05));
          padding: 20px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        h2 {
          color: var(--text-primary, #1f2937);
          font-size: 20px;
          margin: 0;
        }
        .filters {
          display: flex;
          gap: 15px;
          align-items: center;
          flex-wrap: wrap;
        }
        .search-input {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          font-size: 14px;
          width: 200px;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #111827);
        }
        .filter-select {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          font-size: 14px;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #111827);
        }
        .log-item {
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 15px;
          transition: box-shadow 0.2s;
          position: relative;
        }
        .bulk-actions { display:flex; gap:10px; align-items:center; }
        .checkbox { width:16px; height:16px; cursor:pointer; }
        .bulk-delete-btn { padding:8px 12px; border:1px solid #dc2626; color:#fff; background:#dc2626; border-radius:4px; cursor:pointer; font-size:14px; }
        .bulk-delete-btn:disabled { opacity:0.5; cursor:not-allowed; }
        .log-item:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .activity-icon {
          font-size: 18px;
          min-width: 20px;
        }
        .log-content {
          flex: 1;
        }
        .log-description {
          font-size: 14px;
          color: var(--text-primary, #374151);
          margin-bottom: 4px;
        }
        .log-meta {
          font-size: 12px;
          color: var(--text-muted, #6b7280);
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        .severity-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
        }
        .role-badge {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          background: var(--bg-tertiary, #e5e7eb);
          color: var(--text-primary, #374151);
        }
        .log-actions {
          position: absolute;
          top: 10px;
          right: 10px;
          display: flex;
          gap: 5px;
        }
        .delete-log-btn {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid #dc2626;
          background: var(--bg-card, #fff);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          color: #dc2626;
        }
        .delete-log-btn:hover {
          background: #dc2626;
          color: #fff;
        }
        .no-logs {
          text-align: center;
          padding: 40px;
          color: var(--text-muted, #6b7280);
        }
        @media (max-width: 768px) {
          .filters {
            flex-direction: column;
            align-items: stretch;
          }
          .search-input, .filter-select {
            width: 100%;
          }
          .log-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          .log-meta {
            flex-direction: column;
            gap: 5px;
          }
        }
      `}</style>

            <div className="activity-logs">
                <div className="header">
                    <h2>Activity Logs</h2>
                    <div className="filters">
                        <div className="bulk-actions">
                            <input type="checkbox" className="checkbox" checked={selectAll && selectedIds.length === filteredLogs.length && filteredLogs.length > 0} onChange={handleToggleSelectAll} title="Select all" />
                            <button className="bulk-delete-btn" onClick={handleBulkDelete} disabled={!hasRole(ROLES.SUPER_ADMIN) || selectedIds.length === 0}>Delete selected</button>
                        </div>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search activities..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            className="filter-select"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        >
                            <option value="all">All Activities</option>
                            <option value={ACTIVITY_TYPES.CUSTOMER_DELETED}>Customer Deleted</option>
                            <option value={ACTIVITY_TYPES.SERVICEMAN_DELETED}>Serviceman Deleted</option>
                            <option value={ACTIVITY_TYPES.SERVICEMAN_UPDATED}>Serviceman Updated</option>
                            <option value={ACTIVITY_TYPES.SERVICEMAN_APPROVED}>Serviceman Approved</option>
                            <option value={ACTIVITY_TYPES.SERVICEMAN_REJECTED}>Serviceman Rejected</option>
                            <option value={ACTIVITY_TYPES.USER_CREATED}>User Created</option>
                            <option value={ACTIVITY_TYPES.USER_UPDATED}>User Updated</option>
                            <option value={ACTIVITY_TYPES.USER_DELETED}>User Deleted</option>
                            <option value={ACTIVITY_TYPES.USER_PASSWORD_CHANGED}>Password Changed</option>
                            <option value={ACTIVITY_TYPES.LOGIN}>Login</option>
                            <option value={ACTIVITY_TYPES.LOGOUT}>Logout</option>
                        </select>
                    </div>
                </div>

                <div>
                    {filteredLogs.length > 0 ? (
                        filteredLogs.map((log) => (
                            <div key={log.id} className="log-item">
                                <input
                                    type="checkbox"
                                    className="checkbox"
                                    checked={selectedIds.includes(log.id)}
                                    onChange={() => handleToggleSelect(log.id)}
                                    title="Select log"
                                />
                                <div className="activity-icon">
                                    {getActivityIcon(log.activityType)}
                                </div>
                                <div className="log-content">
                                    <div className="log-description">
                                        {getActivityDescription(log)}
                                    </div>
                                    <div className="log-meta">
                                        <span>{log.timestamp}</span>
                                        <span className="role-badge">{log.userRole.replace('_', ' ')}</span>
                                        <span
                                            className="severity-badge"
                                            style={{
                                                backgroundColor: getSeverityColor(log.severity) + '20',
                                                color: getSeverityColor(log.severity)
                                            }}
                                        >
                                            {log.severity}
                                        </span>
                                    </div>
                                </div>
                                {hasRole(ROLES.SUPER_ADMIN) && (
                                    <div className="log-actions">
                                        <button
                                            className="delete-log-btn"
                                            onClick={() => handleDeleteLog(log.id)}
                                            title="Delete this log entry"
                                        >
                                            <FaTrash size={12} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="no-logs">
                            No activity logs found
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivityLogs;
